import { useEffect, useMemo, useState } from 'react';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { gymApi } from '@/lib/api';
import { Reward, RewardProgressEntry } from '@/types';
import { reportError } from '@/lib/sentry';
import { Loader2, Flame, Trophy, Medal, Crown, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RewardParticipantsSheetProps {
    reward: Reward | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const initials = (name: string) =>
    name.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const podiumStyle = (rank: number) =>
    rank === 1 ? 'from-yellow-500/30 to-yellow-500/5 border-yellow-500/50 text-yellow-300'
        : rank === 2 ? 'from-zinc-400/20 to-zinc-400/5 border-zinc-400/40 text-zinc-200'
            : 'from-orange-700/20 to-orange-700/5 border-orange-700/40 text-orange-300';

const podiumHeight = (rank: number) => (rank === 1 ? 'h-24' : rank === 2 ? 'h-16' : 'h-12');

export function RewardParticipantsSheet({ reward, open, onOpenChange }: RewardParticipantsSheetProps) {
    const [progress, setProgress] = useState<RewardProgressEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [editing, setEditing] = useState<{ clientId: string; clientName: string; days: string } | null>(null);
    const [saving, setSaving] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (!open || !reward?.id) return;
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const res = await gymApi.listRewardProgress(reward.id);
                if (!cancelled && res.success) setProgress(res.progress);
            } catch (error) {
                reportError(error, { operation: 'reward_participants_load', area: 'crm', extra: { reward_id: reward.id } });
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, reward?.id, reloadToken]);

    const handleSaveDays = async () => {
        if (!editing || !reward?.id) return;
        const days = Number(editing.days);
        if (!Number.isInteger(days) || days < 0 || days > 10000) {
            toast({
                variant: 'destructive',
                title: 'Valor inválido',
                description: 'Ingresa un número entero de días entre 0 y 10000.',
            });
            return;
        }

        setSaving(true);
        try {
            const res = await gymApi.setRewardProgressDays(reward.id, editing.clientId, days);
            if (res?.success === false) {
                toast({
                    variant: 'destructive',
                    title: 'No se pudo ajustar',
                    description: res.message || 'Intenta de nuevo.',
                });
                return;
            }
            setEditing(null);
            setReloadToken((t) => t + 1);
            toast({
                title: 'Días actualizados',
                description: `${editing.clientName}: ${days} día(s).`,
            });
        } catch (error) {
            reportError(error, {
                operation: 'reward_progress_adjust',
                area: 'crm',
                extra: { reward_id: reward.id, client_id: editing.clientId },
            });
            toast({
                variant: 'destructive',
                title: 'Error',
                description: 'No se pudieron ajustar los días.',
            });
        } finally {
            setSaving(false);
        }
    };

    const entryMap = useMemo(() => {
        const map: Record<string, { is_winner: boolean; claimed: boolean }> = {};
        (reward?.entries || []).forEach((e) => {
            map[e.client_id] = { is_winner: e.is_winner, claimed: !!e.claimed_at };
        });
        return map;
    }, [reward?.entries]);

    const ranked = useMemo(
        () => progress.map((p, i) => ({
            ...p,
            rank: i + 1,
            is_winner: entryMap[p.client_id]?.is_winner ?? false,
            claimed: entryMap[p.client_id]?.claimed ?? false,
            entered: !!entryMap[p.client_id],
        })),
        [progress, entryMap]
    );

    const threshold = reward?.config?.threshold || 0;
    const podium = ranked.slice(0, 3);
    const podiumOrder = [podium[1], podium[0], podium[2]].filter(Boolean);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-md bg-card border-l border-border overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Flame className="h-5 w-5 text-primary" />
                        {reward?.title || 'Racha'}
                    </SheetTitle>
                    <SheetDescription>
                        Podio y participantes — {ranked.length} en la racha
                    </SheetDescription>
                </SheetHeader>

                {isLoading ? (
                    <div className="py-20 flex justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : ranked.length === 0 ? (
                    <div className="py-20 text-center text-sm text-muted-foreground">
                        Aún no hay participantes en esta racha.
                    </div>
                ) : (
                    <div className="mt-6 space-y-6">
                        {/* Podium */}
                        <div className="flex items-end justify-center gap-3">
                            {podiumOrder.map((p) => (
                                <div key={p.client_id} className="flex-1 flex flex-col items-center gap-2">
                                    {p.is_winner && <Trophy className="h-4 w-4 text-yellow-400" />}
                                    <div className={cn('h-14 w-14 rounded-full border-2 bg-gradient-to-b flex items-center justify-center font-black', podiumStyle(p.rank))}>
                                        {initials(p.client_name || '?')}
                                    </div>
                                    <div className={cn('w-full rounded-t-xl border-t-2 bg-gradient-to-b flex flex-col items-center justify-end pb-2', podiumStyle(p.rank), podiumHeight(p.rank))}>
                                        <span className="text-xs font-black">{p.rank}º</span>
                                        <span className="text-sm font-black text-primary">{p.counted_days}d</span>
                                    </div>
                                    <span className="w-full truncate text-center text-[11px] font-bold">{p.client_name || 'Socio'}</span>
                                    {p.entered && (
                                        <Badge variant="secondary" className="px-1.5 py-0 text-[9px]">En la rifa</Badge>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Full ranked list */}
                        <div className="space-y-2">
                            <p className="flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
                                <Medal className="h-3 w-3" /> Todos los participantes
                            </p>
                            {ranked.map((p) => (
                                <div
                                    key={p.client_id}
                                    className={cn(
                                        'space-y-2 rounded-xl border px-3 py-2',
                                        p.is_winner ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-border bg-card'
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-black">
                                            {p.rank}
                                        </span>
                                        <span className="flex-1 truncate text-sm font-bold">{p.client_name || 'Socio'}</span>
                                        {p.is_winner && (
                                            <Badge className="gap-1 border-yellow-500/40 bg-yellow-500/20 px-1.5 py-0 text-[9px] text-yellow-400">
                                                <Crown className="h-3 w-3" /> Ganador
                                            </Badge>
                                        )}
                                        {p.claimed && (
                                            <Badge variant="secondary" className="px-1.5 py-0 text-[9px]">Reclamado</Badge>
                                        )}
                                        {p.entered && !p.is_winner && (
                                            <Badge variant="outline" className="px-1.5 py-0 text-[9px]">En la rifa</Badge>
                                        )}
                                        <span className="text-sm font-black text-primary">{p.counted_days}d</span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                                            title="Ajustar días manualmente"
                                            onClick={() => setEditing({
                                                clientId: p.client_id,
                                                clientName: p.client_name || 'Socio',
                                                days: String(p.counted_days),
                                            })}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                    <Progress
                                        value={threshold ? Math.min(100, (p.counted_days / threshold) * 100) : 0}
                                        className="h-1.5"
                                    />
                                    {!!p.manual_adjustment && (
                                        <p className="text-[10px] font-bold text-muted-foreground">
                                            Ajuste manual: {p.manual_adjustment > 0 ? '+' : ''}{p.manual_adjustment} día(s)
                                            {p.manual_adjustment_at ? ` · ${new Date(p.manual_adjustment_at).toLocaleDateString('es-ES')}` : ''}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </SheetContent>

            <Dialog open={!!editing} onOpenChange={(next) => !next && !saving && setEditing(null)}>
                <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-full sm:max-w-[380px]">
                    <DialogHeader>
                        <DialogTitle>Ajustar días de racha</DialogTitle>
                        <DialogDescription>{editing?.clientName}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-2">
                        <div className="space-y-1">
                            <Label htmlFor="reward-progress-days">Días contados</Label>
                            <Input
                                id="reward-progress-days"
                                type="number"
                                min={0}
                                max={10000}
                                step={1}
                                value={editing?.days ?? ''}
                                onChange={(e) => setEditing((prev) => prev ? { ...prev, days: e.target.value } : prev)}
                                disabled={saving}
                                autoFocus
                            />
                        </div>
                        <p className="text-xs leading-tight text-muted-foreground">
                            El ajuste se guarda como corrección manual y no se pierde con el próximo check-in.
                            Cada día nuevo con asistencia suma uno.
                        </p>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSaveDays} disabled={saving || !editing?.days}>
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Sheet>
    );
}
